import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { Box, Button, Modal, Typography } from '@mui/material';

const FLAG_KEY = 'sharpstack_bettracker_tutorial_v1';
const MOBILE_BREAKPOINT = 900;

const STEPS = [
  {
    target: '[data-tutorial="bettracker-filters"]',
    title: 'Filters & Date Range',
    content:
      'Filter your tracked bets by League, Source, Bet Type, and Book. Use the date range selector to view performance over today, last 7 days, last 30 days, all time, or a custom range.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '[data-tutorial="bettracker-metrics"]',
    title: 'Performance Metrics',
    content:
      'Live stats across your filtered bets: EV (expected edge at time of bet), CLV (closing line value), +/- and +CLV% (how often you beat the close), W/L and W% (actual results), BE% (break-even rate needed to be profitable).',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '.data-grid-container',
    title: 'Line, Odds & Live',
    content:
      'The "Line" and "Odds" columns show the spread/total and price at the moment you tracked the bet — these never change. The "Live" columns next to them show the current line/odds (or the closing values once the game has started).',
    placement: 'center',
    skipBeacon: true,
  },
];

const CustomTooltip = ({
  index, size, step, backProps, primaryProps, skipProps, tooltipProps, isLastStep,
}) => (
  <div {...tooltipProps} style={{ backgroundColor: '#151a28', border: '1px solid #262d40', borderRadius: 12, padding: '20px 22px 16px', width: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'inherit' }}>
    <div style={{ display: 'inline-block', background: '#4caf50', color: '#000', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 100, marginBottom: 10, letterSpacing: 0.5 }}>
      STEP {index + 1} OF 5
    </div>
    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>{step.title}</h3>
    <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>{step.content}</p>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <button {...skipProps} style={{ background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Skip tour</button>
      <div style={{ display: 'flex', gap: 6 }}>
        {index > 0 && (
          <button {...backProps} style={{ background: 'none', border: '1px solid #262d40', color: '#8892a8', padding: '7px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Back</button>
        )}
        <button {...primaryProps} style={{ background: '#4caf50', color: '#000', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Next →
        </button>
      </div>
    </div>
  </div>
);

const joyrideStyles = { options: { zIndex: 10000, overlayColor: 'rgba(0, 0, 0, 0.72)', arrowColor: '#151a28' } };

export default function BetTrackerTutorial() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);
  // 0 = none, 1 = CLV (step 4), 2 = Actual (step 5)
  const [customStep, setCustomStep] = useState(0);

  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (localStorage.getItem(FLAG_KEY)) return;
    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleReplay = (e) => {
      if (e.detail?.flag !== FLAG_KEY) return;
      setRunTour(false);
      setCustomStep(0);
      setShowWelcome(true);
    };
    window.addEventListener('sharpstack:replay-tutorial', handleReplay);
    return () => window.removeEventListener('sharpstack:replay-tutorial', handleReplay);
  }, []);

  const startTour = () => { setShowWelcome(false); setTimeout(() => setRunTour(true), 200); };
  const skipTour = () => { setShowWelcome(false); localStorage.setItem(FLAG_KEY, 'true'); };

  const finishTutorial = () => {
    setCustomStep(0);
    localStorage.setItem(FLAG_KEY, 'true');
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.SKIPPED) {
      localStorage.setItem(FLAG_KEY, 'true');
      setRunTour(false);
      return;
    }
    if (status === STATUS.FINISHED) {
      // Joyride done — transition to custom CLV step
      setRunTour(false);
      setCustomStep(1);
    }
  };

  return (
    <>
      <Modal open={showWelcome} onClose={() => {}} disableEscapeKeyDown>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#141820', border: '1px solid #252a38', borderRadius: '16px', p: '44px 40px 36px', width: 440, maxWidth: '90vw', textAlign: 'center', outline: 'none', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '14px', background: 'linear-gradient(135deg, #4caf50, #2e7d32)', margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: '#000', fontWeight: 800 }}>S</Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>Bet Tracker</Typography>
          <Typography sx={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, mb: 4, px: 1 }}>
            Track every bet you place across all books and see how your picks are performing over time. Let's show you how.
          </Typography>
          <Button onClick={startTour} sx={{ backgroundColor: '#4caf50', color: '#000', fontWeight: 700, px: 4, py: 1.25, fontSize: 14, textTransform: 'none', borderRadius: 2, '&:hover': { backgroundColor: '#66bb6a' } }}>Show Me</Button>
          <Box sx={{ mt: 1.5 }}>
            <Button onClick={skipTour} sx={{ color: '#4a5068', fontSize: 13, textTransform: 'none', '&:hover': { color: '#94a3b8', background: 'none' } }}>I've got it</Button>
          </Box>
        </Box>
      </Modal>
      <Joyride steps={STEPS} run={runTour} onEvent={handleJoyrideCallback} continuous disableOverlayClose tooltipComponent={CustomTooltip} styles={joyrideStyles} />

      {customStep === 1 && (
        <ColumnStep
          fieldName="lineValue"
          stepNumber={4}
          title="CLV — Your Edge Indicator"
          content="CLV (Closing Line Value) measures the gap between your tracked Odds and the Live/closing Odds. Consistently positive CLV is the strongest signal of long-term edge — more reliable than win rate or ROI in small samples."
          nextLabel="Next →"
          onNext={() => setCustomStep(2)}
        />
      )}
      {customStep === 2 && (
        <ColumnStep
          fieldName="actual"
          stepNumber={5}
          title="Actual Results"
          content={'The "Actual" column shows the result and how each bet cashed (won/lost/push). Results are graded and updated overnight after games complete — check back the next morning to see updated actuals, CLV, and win/loss stats.'}
          nextLabel="Got it ✓"
          onNext={finishTutorial}
        />
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Custom column step — measured cutout on a specific column.
// Modeled on EVTutorial's BetStep. Bypasses Joyride entirely to avoid
// scroll-parent fights with MUI DataGrid's virtualized scroller.
// ───────────────────────────────────────────────────────────────────────────

function ColumnStep({ fieldName, stepNumber, title, content, nextLabel, onNext }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const measure = () => {
      const h = document.querySelector(`.MuiDataGrid-columnHeader[data-field="${fieldName}"]`)?.getBoundingClientRect();
      const g = document.querySelector('.data-grid-container')?.getBoundingClientRect();
      if (h && g) setRect({ left: h.left, top: h.top, width: h.width, height: g.bottom - h.top });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fieldName]);

  const clip = rect ? `polygon(
    0 0, 100vw 0, 100vw 100vh, 0 100vh, 0 ${rect.top}px,
    ${rect.left}px ${rect.top}px,
    ${rect.left}px ${rect.top + rect.height}px,
    ${rect.left + rect.width}px ${rect.top + rect.height}px,
    ${rect.left + rect.width}px ${rect.top}px,
    0 ${rect.top}px
  )` : 'none';

  // Position the popup just to the left of the highlighted column.
  // Clamp to 20px from the left edge in case the column is near the left side.
  const popupLeft = rect ? Math.max(20, rect.left - 360) : 24;
  const popupTop = rect ? Math.max(80, rect.top) : 180;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', clipPath: clip, zIndex: 9998 }} />
      <div style={{ position: 'fixed', top: popupTop, left: popupLeft, background: '#151a28', border: '1px solid #262d40', borderRadius: 12, padding: '20px 22px 16px', width: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', zIndex: 10000 }}>
        <div style={{ display: 'inline-block', background: '#4caf50', color: '#000', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 100, marginBottom: 10, letterSpacing: 0.5 }}>
          STEP {stepNumber} OF 5
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>{content}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onNext} style={{ background: '#4caf50', color: '#000', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {nextLabel}
          </button>
        </div>
      </div>
    </>
  );
}